import { useEffect, useRef } from 'react';
import {
    FORCED_LOGOUT_EVENT,
    FORCED_LOGOUT_STORAGE_KEY,
    handleForcedLogout,
    isForcedLogoutError,
    isSessionRevokedError
} from './forcedLogout';
import { SIGNED_OUT_EVENT, logoutProfile, signedOutStorageKey } from './globalLogout';

const isAuthPath = () =>
    typeof window !== 'undefined' && window.location.pathname.startsWith('/auth');
const isLocalDevelopmentHost = () =>
    typeof window !== 'undefined' &&
    ['localhost', '127.0.0.1', '[::1]'].includes(window.location.hostname);

const useForcedLogoutSession = ({
    enabled = true,
    token,
    client,
    onForcedLogout,
    onCleanup,
    loginUrl,
    pollingEndpoint,
    pollingIntervalMs = 5000,
    skipOnAuthRoutes = true,
    skipOnLocalhost = false
}) => {
    const latest = useRef({ onCleanup, loginUrl });
    latest.current = { onCleanup, loginUrl };
    useEffect(() => {
        if (typeof window === 'undefined') return undefined;
        const cleanup = () => {
            if (typeof onForcedLogout === 'function') onForcedLogout();
        };
        const environment = logoutProfile().environment;
        const handleStorageEvent = (event) => {
            if (event.key === signedOutStorageKey(environment) && event.newValue) {
                handleForcedLogout({ broadcast: false, onCleanup, loginUrl, reason: 'signed-out' });
            } else if (event.key === FORCED_LOGOUT_STORAGE_KEY && event.newValue) {
                handleForcedLogout({
                    broadcast: false,
                    onCleanup,
                    loginUrl,
                    reason: event.newValue === 'signed-out' ? 'signed-out' : 'forced'
                });
            }
        };
        window.addEventListener(FORCED_LOGOUT_EVENT, cleanup);
        window.addEventListener(SIGNED_OUT_EVENT, cleanup);
        window.addEventListener('storage', handleStorageEvent);
        return () => {
            window.removeEventListener(FORCED_LOGOUT_EVENT, cleanup);
            window.removeEventListener(SIGNED_OUT_EVENT, cleanup);
            window.removeEventListener('storage', handleStorageEvent);
        };
    }, [onForcedLogout, onCleanup, loginUrl]);

    useEffect(() => {
        if (
            !enabled ||
            !token ||
            typeof window === 'undefined' ||
            !client ||
            typeof client.get !== 'function' ||
            !pollingEndpoint ||
            (skipOnAuthRoutes && isAuthPath()) ||
            (skipOnLocalhost && isLocalDevelopmentHost())
        )
            return undefined;
        const baseDelay = Math.min(10000, Math.max(5000, pollingIntervalMs));
        let timer = null;
        let disposed = false;
        let pending = false;
        let failures = 0;
        let lastStarted = 0;
        const stop = () => {
            if (timer !== null) window.clearTimeout(timer);
            timer = null;
        };
        const schedule = () => {
            stop();
            if (!disposed && !document.hidden)
                timer = window.setTimeout(check, Math.min(30000, baseDelay * 2 ** failures));
        };
        const check = async () => {
            if (pending || disposed || document.hidden) return;
            stop();
            pending = true;
            lastStarted = Date.now();
            try {
                await client.get(pollingEndpoint, { headers: { token } });
                failures = 0;
            } catch (error) {
                failures = Math.min(3, failures + 1);
                // Also support clients without the shared interceptor. Cleanup is local;
                // only explicit Sign Out actions call Core's revocation endpoint.
                if (isForcedLogoutError(error))
                    handleForcedLogout({
                        ...latest.current,
                        reason: isSessionRevokedError(error) ? 'signed-out' : 'forced'
                    });
            } finally {
                pending = false;
                schedule();
            }
        };
        const resume = () => {
            if (document.hidden) {
                stop();
                return;
            }
            if (Date.now() - lastStarted >= 1000) void check();
            else schedule();
        };
        void check();
        document.addEventListener('visibilitychange', resume);
        window.addEventListener('focus', resume);
        return () => {
            disposed = true;
            stop();
            document.removeEventListener('visibilitychange', resume);
            window.removeEventListener('focus', resume);
        };
    }, [
        enabled,
        token,
        client,
        pollingEndpoint,
        pollingIntervalMs,
        skipOnAuthRoutes,
        skipOnLocalhost
    ]);
};
export default useForcedLogoutSession;
